import React from 'react';
import { Box, TextField, Button, Typography, Chip } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { inputStyles } from '../styles';
import useAlias from '@src/hooks/useAlias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

interface SelectFieldFormItemProps {
  newOption: string;
  parsedOptions: string[];
  newFieldData: {
    defaultValue: string | number | boolean | null;
  };
  newFieldErrors: Record<string, string>;
  setNewOption: (option: string) => void;
  handleAddOption: () => void;
  handleRemoveOption: (option: string) => void;
  handleNewFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SelectFieldFormItem: React.FC<SelectFieldFormItemProps> = ({
  newOption,
  parsedOptions,
  newFieldData,
  newFieldErrors,
  setNewOption,
  handleAddOption,
  handleRemoveOption,
  handleNewFieldChange,
}) => {
  const alias = useAlias();

  return (
    <>
      <Grid2 size={12}>
        <Typography variant='body2' gutterBottom>
          {alias('Options')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <TextField
            label={alias('Add option')}
            name='newOption'
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            variant='outlined'
            size='small'
            sx={{
              ...inputStyles,
              flexGrow: 1,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddOption();
              }
            }}
          />
          <Button
            variant='contained'
            color='primary'
            onClick={handleAddOption}
            size='small'
          >
            <FormattedAlias id='Add' />
          </Button>
        </Box>
        {!!newFieldErrors['listOptions'] && (
          <Typography
            variant='caption'
            color='error'
            sx={{ display: 'block', mb: 1 }}
          >
            {newFieldErrors['listOptions']}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {parsedOptions.map((option, index) => (
            <Chip
              key={`option-${index}-${option}`}
              label={option}
              onDelete={() => handleRemoveOption(option)}
              color='default'
              variant='outlined'
            />
          ))}
        </Box>
      </Grid2>

      {/* Display chips for options */}
      {parsedOptions.length > 0 && (
        <Grid2 size={12} sx={{ mt: 2 }}>
          <Typography variant='body2' gutterBottom>
            {alias('Select Default Option:')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
            }}
          >
            {parsedOptions.map((option, index) => (
              <Chip
                key={`new-option-${index}-${option}`}
                label={option}
                onClick={() => {
                  // Set this option as the default value
                  handleNewFieldChange({
                    target: {
                      name: 'defaultValue',
                      value: option,
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                color={
                  newFieldData.defaultValue === option ? 'primary' : 'default'
                }
                variant={
                  newFieldData.defaultValue === option ? 'filled' : 'outlined'
                }
              />
            ))}
          </Box>
        </Grid2>
      )}
    </>
  );
};

export default SelectFieldFormItem;
